import React from 'react';

const arrowPillStyle = {
  cursor: 'pointer',
  userSelect: 'none',
  fontSize: '12px',
  color: '#fff',
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  padding: '2px 6px',
  lineHeight: 1,
  borderRadius: '2px',
};

// Used only where thumbnails are especially small (SummaryCaseTable's grid) and the default
// pill is too big for the cell.
const compactArrowPillStyle = {
  ...arrowPillStyle,
  fontSize: '10px',
  padding: '1px 5px',
};

// Small arrows overlaid on a photo thumbnail (grid cells). `position` picks bottom corners
// (default), top corners, or vertically centered on the left/right edges. `size="compact"`
// shrinks the pill for especially small thumbnails.
export const PhotoOverlayArrows = ({ onPrev, onNext, position = 'bottom', size = 'normal' }) => {
  const pillStyle = size === 'compact' ? compactArrowPillStyle : arrowPillStyle;
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        ...(position === 'center' ? { top: '50%', transform: 'translateY(-50%)' } : { [position]: 2 }),
        left: 2,
        right: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      <span onClick={onPrev} style={{ ...pillStyle, pointerEvents: 'auto' }}>{'<'}</span>
      <span onClick={onNext} style={{ ...pillStyle, pointerEvents: 'auto' }}>{'>'}</span>
    </div>
  );
};

// Small "n / total" badge overlaid on a photo thumbnail's bottom edge — a non-interactive
// companion to PhotoOverlayArrows for thumbnails that want the count visible without opening
// the enlarged view.
export const PhotoCountBadge = ({ current, total }) => (
  <div
    style={{
      position: 'absolute',
      bottom: 2,
      left: '50%',
      transform: 'translateX(-50%)',
      fontSize: '11px',
      color: '#fff',
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
      padding: '1px 6px',
      borderRadius: '2px',
      lineHeight: 1.4,
      userSelect: 'none',
      pointerEvents: 'none',
      zIndex: 2,
    }}
  >
    {current} / {total}
  </div>
);

// Arrows + "n / total" counter rendered as one pill below an enlarged photo.
export const PhotoCounterBar = ({ onPrev, onNext, current, total }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      fontSize: '14px',
      color: '#fff',
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
      padding: '4px 12px',
      borderRadius: '4px',
      marginTop: 12,
    }}
  >
    <span onClick={onPrev} style={{ cursor: 'pointer', padding: '0 4px', userSelect: 'none' }}>{'<'}</span>
    <span style={{ userSelect: 'none' }}>{current} / {total}</span>
    <span onClick={onNext} style={{ cursor: 'pointer', padding: '0 4px', userSelect: 'none' }}>{'>'}</span>
  </div>
);

const sideArrowStyle = {
  cursor: 'pointer',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  flexShrink: 0,
  fontSize: '20px',
  lineHeight: 1,
  color: '#fff',
  background: 'rgba(0, 0, 0, 0.55)',
  border: 'none',
  borderRadius: '50%',
};

// Arrows flanking the photo (outside its bounds) with an optional counter underneath — used where
// the photo itself should stay clear of any overlay.
export const PhotoSideArrows = ({ onPrev, onNext, current, total, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <button type="button" onClick={onPrev} style={sideArrowStyle} aria-label="Previous photo">{'‹'}</button>
      {children}
      <button type="button" onClick={onNext} style={sideArrowStyle} aria-label="Next photo">{'›'}</button>
    </div>
    {total > 1 && (
      <span style={{ fontSize: 13, color: '#fff', background: 'rgba(0, 0, 0, 0.55)', padding: '2px 10px', borderRadius: 4, userSelect: 'none' }}>
        {current} / {total}
      </span>
    )}
  </div>
);
